import { useEffect } from 'react';
import { useTranslation } from '../../../i18n';
import { Loading } from '../../../components/Shared/Loading';
import { EmptyState } from '../../../components/Shared/EmptyState';
import { selectObjectTasks, useTaskStore } from '../../../store/useTaskStore';

interface TaskPickerTabProps {
  objectId: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

/** Multi-select of the object's active tasks; each becomes a position linked to its task. */
export function TaskPickerTab({ objectId, selectedIds, onChange }: TaskPickerTabProps) {
  const { t } = useTranslation('offers');
  const { fetchObjectTasks, loading } = useTaskStore();
  const tasks = useTaskStore(selectObjectTasks(objectId ?? undefined, true));

  useEffect(() => {
    if (objectId) void fetchObjectTasks(objectId, { activeOnly: true });
  }, [objectId, fetchObjectTasks]);

  const selected = new Set(selectedIds);
  const allSelected = tasks.length > 0 && selected.size === tasks.length;

  const toggle = (taskId: string) => {
    const next = new Set(selected);
    if (next.has(taskId)) next.delete(taskId);
    else next.add(taskId);
    onChange([...next]);
  };

  if (loading) return <Loading />;

  if (tasks.length === 0) {
    return (
      <EmptyState
        title={t('offers:positions.taskList.empty')}
        description={t('offers:positions.taskList.emptyHint')}
      />
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[12px]">
        <button
          type="button"
          onClick={() => onChange(allSelected ? [] : tasks.map((task) => task.taskId))}
          className="text-[#00A871] hover:underline"
        >
          {allSelected ? t('offers:positions.taskList.deselectAll') : t('offers:positions.taskList.selectAll')}
        </button>
        {selected.size > 0 && (
          <span className="text-slate-500 tabular-nums">
            {t('offers:positions.taskList.selectedCount', { count: selected.size })}
          </span>
        )}
      </div>

      <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200">
        {tasks.map((task) => (
          <label
            key={task.taskId}
            className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selected.has(task.taskId)}
              onChange={() => toggle(task.taskId)}
              className="h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
            />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium text-slate-900">{task.name}</div>
              <div className="text-[11px] text-slate-500">
                {task.serviceName}
                {task.estimatedDurationMinutes > 0 && (
                  <span className="tabular-nums">
                    {' · '}
                    {t('offers:positions.taskList.minutes', { count: task.estimatedDurationMinutes })}
                  </span>
                )}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}
